<template>
    <div v-if="model">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo"
                            :range-inline="false"
                            :start-validations="validations.periodo"
                            end-label="Até"
                            range
                            start-label="Período de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.unidades"
                            @change="handleUnidadeChange"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.infraestruturas"
                            @change="handleInfraestruturaChange"
                            default-option-text="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.areas"
                            @change="handleAreaChange"
                            default-option-text="Todas"
                            label="Área"
                            show-default-option
                            text-field="descricao"
                            v-model="model.areaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.microareas"
                            @change="handleMicroareaChange"
                            default-option-text="Todas"
                            label="Microárea"
                            show-default-option
                            text-field="descricao"
                            v-model="model.microareaId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.usuarios"
                            @change="handleUsuarioChange"
                            default-option-text="Todos"
                            label="Agente responsável"
                            show-default-option
                            text-field="nome"
                            v-model="model.usuarioId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :repository="repositories.relatorioVisitaDomiciliar"
                            :request-query-string-params="{
                                periodo: {
                                    start: others.moment(model.periodo.start).format('DD/MM/YYYY'),
                                    end: others.moment(model.periodo.end).format('DD/MM/YYYY'),
                                },
                                unidade_id: model.unidadeId,
                                infraestrutura_id: model.infraestruturaId,
                                area_id: model.areaId,
                                microarea_id: model.microareaId,
                                usuario_id: model.usuarioId,
                            }"
                            :search-fields="fields.autocomplete"
                            :text-field="(item) =>  item.cns ? `${this.formatCns(item.cns)} - ${item.nome}` : item.nome"
                            label="Paciente"
                            v-model="model.paciente"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.domicilios"
                            @change="handleDomicilioChange"
                            default-option-text="Todos"
                            label="Logradouro"
                            show-default-option
                            text-field="descricao"
                            v-model="model.domicilioId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-input
                            @change="handleEnderecoNumeroChange"
                            label="Número do logradouro"
                            v-model="model.enderecoNumero"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-select
                            :options="form.desfechos"
                            default-option-text="Todos"
                            label="Desfecho"
                            show-default-option
                            text-field="descricao"
                            v-model="model.desfechoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <template v-if="data">
            <rkm-area
                no-margin-top
                title="Resultado"
            >
                <template v-slot:trailing>
                    <rkm-button
                        @click="print"
                        color="info"
                        small
                        title="Imprimir"
                    >
                        <rkm-icon icon="print" />
                    </rkm-button>

                    <rkm-button
                        @click="exportTo"
                        color="info"
                        small
                        title="Exportar"
                    >
                        <rkm-icon icon="file-export" />
                    </rkm-button>
                </template>

                <rkm-table
                    :fields="fields.table"
                    :items="data.itens"
                    :sort-by="sortBy"
                    :sort-desc="sortDesc"
                    @sort-changed="onSortChanged"
                    hide-pagination
                />
            </rkm-area>

            <rkm-area
                no-margin-top
                title="Totais"
                type="fieldset"
            >
                <rkm-row>
                    <rkm-col>
                        <rkm-form-input
                            :value="data.totalizadores.quantidadeVisitasRealizadas"
                            disabled
                            label="Total de visitas realizadas"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            :value="data.totalizadores.quantidadeAusencias"
                            disabled
                            label="Total de ausências"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            :value="data.totalizadores.quantidadeRecusas"
                            disabled
                            label="Total de recusas"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            :value="data.totalizadores.quantidadeVisitasRegistradas"
                            disabled
                            label="Total de visitas registradas"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            :value="data.totalizadores.quantidadePacientesAtendidos"
                            disabled
                            label="Total de pacientes atendidos"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-area>
        </template>
    </div>
</template>

<script>
// Mixins.
import {error, file, formats, repository, stringCase} from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import {mapActions} from 'vuex'

export default {
    computed: {
        validations() {
            return {
                periodo: {required: true},
            }
        },
    },
    created() {
        this.repositories = {
            exportacao: this.getConectaSusRepository('exportacao.atencaoBasica'),
            impressao: this.getConectaSusRepository('impressao.atencaoBasica'),
            relatorioVisitaDomiciliar: this.getConectaSusRepository('atencaoBasica.relatorioVisitaDomiciliar'),
        }

        this.load()
    },
    data() {
        return {
            form: null,
            fields: {
                autocomplete: [
                    {key: 'cadastro_cidadao.nome', searchable: true},
                    {key: 'cadastro_cidadao.data_nascimento', searchable: true},
                    {key: 'cadastro_paciente.prontuario', searchable: true},
                ],
                table: [
                    {key: 'data', label: 'Data', sortable: true},
                    {key: "pacienteNome", label: "Paciente", sortable: true},
                    {key: 'pacienteProntuario', label: 'Prontuário', sortable: true},
                    {key: 'pacienteEndereco', label: 'Endereço completo', sortable: true},
                    {key: 'usuarioNome', label: 'Agente responsável', sortable: true},
                    {key: 'areaDescricao', label: 'Área', sortable: true},
                    {key: 'microareaDescricao', label: 'Microarea', sortable: true},
                    {key: "residentes", label: "Residentes", sortable: true},
                    {key: "responsavelFamiliar", label: "Responsável familiar", sortable: true},
                    {key: "desfechoDescricao", label: "Desfecho", sortable: true },
                ],
            },
            data: null,
            model: null,
            others: {moment},
            sortBy: 'data',
            sortDesc: false,
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.relatorioVisitaDomiciliar.create()
                .then((response) => {
                    this.form = response.data.form
                    this.model = response.data.model

                    this.addWatch()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        search() {
            this.data = null

            const request = {
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                areaId: this.model.areaId,
                microareaId: this.model.microareaId,
                usuarioId: this.model.usuarioId,
                pacienteId: this.model.paciente ? this.model.paciente.id : null,
                domicilioId: this.model.domicilioId,
                enderecoNumero: this.model.enderecoNumero,
                desfechoId: this.model.desfechoId,
            }

            this.setLoading()

            this.repositories.relatorioVisitaDomiciliar.index(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => this.data = Object.assign(response.data))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {
            this.form.unidades = []
            this.form.infraestruturas = []
            this.form.areas = []
            this.form.microareas = []
            this.form.usuarios = []
            this.form.domicilios = []
            this.form.desfechos = []

            this.model.periodo = {
                start: null,
                end: null,
            }

            this.model.unidadeId = null
            this.model.infraestruturaId = null
            this.model.areaId = null
            this.model.microareaId = null
            this.model.usuarioId = null
            this.model.paciente = null
            this.model.domicilioId = null
            this.model.enderecoNumero = null
            this.model.desfechoId = null

            this.data = null

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        print() {
            this.setLoading()

            const request = {
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                areaId: this.model.areaId,
                microareaId: this.model.microareaId,
                usuarioId: this.model.usuarioId,
                pacienteId: this.model.paciente ? this.model.paciente.id : null,
                domicilioId: this.model.domicilioId,
                enderecoNumero: this.model.enderecoNumero,
                desfechoId: this.model.desfechoId,
            }

            if (this.sortBy) {
                request.sortBy = this.camelToSnake(this.sortBy)
                request.sortDesc = this.sortDesc
            }

            this.repositories.impressao.relatorioVisitaDomiciliar(this.camelObjectKeysToSnakeRecursive(request))
                .then(({data, headers}) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        exportTo() {
            this.setLoading()

            const request = {
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                areaId: this.model.areaId,
                microareaId: this.model.microareaId,
                usuarioId: this.model.usuarioId,
                pacienteId: this.model.paciente ? this.model.paciente.id : null,
                domicilioId: this.model.domicilioId,
                enderecoNumero: this.model.enderecoNumero,
                desfechoId: this.model.desfechoId,
            }

            if (this.sortBy) {
                request.sortBy = this.camelToSnake(this.sortBy)
                request.sortDesc = this.sortDesc
            }

            this.repositories.exportacao.relatorioVisitaDomiciliar(this.camelObjectKeysToSnakeRecursive(request))
                .then(({data, headers}) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        handleUnidadeChange(value) {
            this.form.infraestruturas = []
            this.form.areas = []
            this.form.microareas = []
            this.form.usuarios = []
            this.form.domicilios = []
            this.form.desfechos = []
            this.model.infraestruturaId = null
            this.model.areaId = null
            this.model.microareaId = null
            this.model.usuarioId = null
            this.model.paciente = null
            this.model.domicilioId = null
            this.model.enderecoNumero = null
            this.model.desfechoId = null
            this.data = null

            const request = {
                listar: 'infraestruturas',
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: value,
            }

            this.setLoading()

            this.repositories.relatorioVisitaDomiciliar.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.infraestruturas = Object.assign(response.data.infraestruturas)
                    this.form.areas = Object.assign(response.data.areas)
                    this.form.microareas = Object.assign(response.data.microareas)
                    this.form.usuarios = Object.assign(response.data.usuarios)
                    this.form.domicilios = Object.assign(response.data.domicilios)
                    this.form.desfechos = Object.assign(response.data.desfechos)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleInfraestruturaChange(value) {
            this.form.areas = []
            this.form.microareas = []
            this.form.usuarios = []
            this.form.domicilios = []
            this.form.desfechos = []
            this.model.areaId = null
            this.model.microareaId = null
            this.model.usuarioId = null
            this.model.paciente = null
            this.model.domicilioId = null
            this.model.enderecoNumero = null
            this.model.desfechoId = null
            this.data = null

            const request = {
                listar: 'areas',
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: value,
            }

            this.setLoading()

            this.repositories.relatorioVisitaDomiciliar.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.areas = Object.assign(response.data.areas)
                    this.form.microareas = Object.assign(response.data.microareas)
                    this.form.usuarios = Object.assign(response.data.usuarios)
                    this.form.domicilios = Object.assign(response.data.domicilios)
                    this.form.desfechos = Object.assign(response.data.desfechos)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleAreaChange(value) {
            this.form.microareas = []
            this.form.usuarios = []
            this.form.domicilios = []
            this.form.desfechos = []
            this.model.microareaId = null
            this.model.usuarioId = null
            this.model.paciente = null
            this.model.domicilioId = null
            this.model.enderecoNumero = null
            this.model.desfechoId = null
            this.data = null

            const request = {
                listar: 'microareas',
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                areaId: value,
            }

            this.setLoading()

            this.repositories.relatorioVisitaDomiciliar.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.microareas = Object.assign(response.data.microareas)
                    this.form.usuarios = Object.assign(response.data.usuarios)
                    this.form.domicilios = Object.assign(response.data.domicilios)
                    this.form.desfechos = Object.assign(response.data.desfechos)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleMicroareaChange(value) {
            this.form.usuarios = []
            this.form.domicilios = []
            this.form.desfechos = []
            this.model.usuarioId = null
            this.model.paciente = null
            this.model.domicilioId = null
            this.model.enderecoNumero = null
            this.model.desfechoId = null
            this.data = null

            const request = {
                listar: 'usuarios',
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                areaId: this.model.areaId,
                microareaId: value,
            }

            this.setLoading()

            this.repositories.relatorioVisitaDomiciliar.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.usuarios = Object.assign(response.data.usuarios)
                    this.form.domicilios = Object.assign(response.data.domicilios)
                    this.form.desfechos = Object.assign(response.data.desfechos)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleUsuarioChange(value) {
            this.form.domicilios = []
            this.form.desfechos = []
            this.model.paciente = null
            this.model.domicilioId = null
            this.model.enderecoNumero = null
            this.model.desfechoId = null
            this.data = null

            const request = {
                listar: 'domicilios',
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                areaId: this.model.areaId,
                microareaId: this.model.microareaId,
                usuarioId: value,
            }

            this.setLoading()

            this.repositories.relatorioVisitaDomiciliar.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.domicilios = Object.assign(response.data.domicilios)
                    this.form.desfechos = Object.assign(response.data.desfechos)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleDomicilioChange(value) {
            this.form.desfechos = []
            this.model.enderecoNumero = null
            this.model.desfechoId = null
            this.data = null

            const request = {
                listar: 'desfechos',
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                areaId: this.model.areaId,
                microareaId: this.model.microareaId,
                usuarioId: this.model.usuarioId,
                pacienteId: this.model.paciente ? this.model.paciente.id : null,
                domicilioId: value,
            }

            this.setLoading()

            this.repositories.relatorioVisitaDomiciliar.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.desfechos = Object.assign(response.data.desfechos)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleEnderecoNumeroChange(value) {
            this.form.desfechos = []
            this.model.desfechoId = null
            this.data = null

            const request = {
                listar: 'desfechos',
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                areaId: this.model.areaId,
                microareaId: this.model.microareaId,
                usuarioId: this.model.usuarioId,
                pacienteId: this.model.paciente ? this.model.paciente.id : null,
                domicilioId: this.model.domicilioId,
                enderecoNumero: value,
            }

            this.setLoading()

            this.repositories.relatorioVisitaDomiciliar.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.desfechos = Object.assign(response.data.desfechos)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleDesfechoChange() {
            this.data = null
        },
        onSortChanged(context) {
            this.sortBy = context.sortBy
            this.sortDesc = context.sortDesc
        },
        addWatch() {
            this.$watch('model.paciente', function(value) {
                this.form.domicilios = []
                this.form.desfechos = []
                this.model.domicilioId = null
                this.model.enderecoNumero = null
                this.model.desfechoId = null
                this.data = null

                const request = {
                    listar: 'domicilios',
                    periodo: {
                        start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                        end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                    },
                    unidadeId: this.model.unidadeId,
                    infraestruturaId: this.model.infraestruturaId,
                    areaId: this.model.areaId,
                    microareaId: this.model.microareaId,
                    usuarioId: this.model.usuarioId,
                    pacienteId: value ? value.id : null,
                }

                this.setLoading()

                this.repositories.relatorioVisitaDomiciliar.list(this.camelObjectKeysToSnakeRecursive(request))
                    .then(response => {
                        this.form.domicilios = Object.assign(response.data.domicilios)
                        this.form.desfechos = Object.assign(response.data.desfechos)
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            })
        },
    },
    mixins: [error, file, formats, repository, stringCase],
    watch: {
        'model.periodo': {
            deep: true,
            handler(value) {
                this.form.unidades = []
                this.form.infraestruturas = []
                this.form.areas = []
                this.form.microareas = []
                this.form.usuarios = []
                this.form.domicilios = []
                this.form.desfechos = []
                this.model.unidadeId = null
                this.model.infraestruturaId = null
                this.model.areaId = null
                this.model.microareaId = null
                this.model.usuarioId = null
                this.model.paciente = null
                this.model.domicilioId = null
                this.model.enderecoNumero = null
                this.model.desfechoId = null
                this.data = null

                if (value.start === null || value.end === null)  {
                    return
                }

                const request = {
                    listar: 'unidades',
                    periodo: {
                        start: this.others.moment(value.start).format('DD/MM/YYYY'),
                        end: this.others.moment(value.end).format('DD/MM/YYYY'),
                    },
                }

                this.setLoading()

                this.repositories.relatorioVisitaDomiciliar.list(this.camelObjectKeysToSnakeRecursive(request))
                    .then(response => {
                        this.form.unidades = Object.assign(response.data.unidades)
                        this.form.infraestruturas = Object.assign(response.data.infraestruturas)
                        this.form.areas = Object.assign(response.data.areas)
                        this.form.microareas = Object.assign(response.data.microareas)
                        this.form.usuarios = Object.assign(response.data.usuarios)
                        this.form.domicilios = Object.assign(response.data.domicilios)
                        this.form.desfechos = Object.assign(response.data.desfechos)
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            },
        },
    },
}
</script>